import { useEffect } from "react";

export const SITE_ORIGIN = "https://www.salesby.us";

interface PageSeoOptions {
  /** Route path, e.g. "/about". Used for canonical + og:url. */
  path: string;
  title: string;
  description: string;
  keywords?: string;
  ogType?: "website" | "article";
  /** Private / utility pages that should stay out of search results. */
  noindex?: boolean;
}

const upsertMeta = (selector: string, attr: string, value: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href: string) => {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

/**
 * Keeps <title>, description, canonical and social tags in sync with the
 * route the user is on. Client-side navigation otherwise leaves the static
 * index.html tags in place, making every page look like the homepage.
 */
export const usePageSeo = ({
  path,
  title,
  description,
  keywords,
  ogType = "website",
  noindex = false,
}: PageSeoOptions) => {
  useEffect(() => {
    const url = `${SITE_ORIGIN}${path === "/" ? "/" : path}`;

    document.title = title;
    upsertMeta('meta[name="title"]', "name", "title", title);
    upsertMeta('meta[name="description"]', "name", "description", description);
    if (keywords) upsertMeta('meta[name="keywords"]', "name", "keywords", keywords);

    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[property="og:url"]', "property", "og:url", url);
    upsertMeta('meta[property="og:type"]', "property", "og:type", ogType);

    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    upsertMeta('meta[name="twitter:url"]', "name", "twitter:url", url);

    setCanonical(url);

    const robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const previousRobots = robots?.getAttribute("content") ?? null;
    if (noindex) {
      upsertMeta('meta[name="robots"]', "name", "robots", "noindex, nofollow");
    }

    return () => {
      if (noindex && previousRobots !== null) {
        upsertMeta('meta[name="robots"]', "name", "robots", previousRobots);
      }
    };
  }, [path, title, description, keywords, ogType, noindex]);
};
